/**
 * Voice dictation for the Ask Dink question box (DNKADM-0462).
 *
 * Browser speech recognition only (the Web Speech API: `SpeechRecognition` /
 * `webkitSpeechRecognition`). Nothing is recorded, sent to Dink, or stored by us; the
 * words go into the box and the operator reads them, edits, and presses Send. It never
 * sends by itself. Where the browser has no recognition (Firefox) `supported` is false
 * and the mic is not shown; a blocked microphone sets `denied` and the mic goes away.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** Stop listening after this long with no words. */
export const VOICE_SILENCE_MS = 8000;

/** The slice of the Web Speech API this uses (not in lib.dom). */
interface SpeechRecognitionResultLike {
  readonly 0: { readonly transcript: string };
}
interface SpeechRecognitionEventLike {
  readonly results: ArrayLike<SpeechRecognitionResultLike>;
}
interface SpeechRecognitionErrorEventLike {
  readonly error: string;
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function recognitionCtor(): SpeechRecognitionCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** The browser's language, `en-US` by default (the console has no locale setting). */
function recognitionLang(): string {
  return navigator.language || 'en-US';
}

export interface VoiceDictation {
  supported: boolean;
  listening: boolean;
  denied: boolean;
  toggle: () => void;
  /** Ends listening; the words already heard stay in the box. */
  stop: () => void;
  /** Ends listening and drops anything still on its way (after Send or New chat). */
  cancel: () => void;
}

/**
 * @param getBase the box's text when listening starts; dictation is appended to it.
 * @param onText receives the whole box text (base plus the words so far).
 */
export function useVoiceDictation(getBase: () => string, onText: (text: string) => void) {
  const [supported] = useState(() => recognitionCtor() !== null);
  const [listening, setListening] = useState(false);
  const [denied, setDenied] = useState(false);
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const timerRef = useRef<number | null>(null);

  // Stable (refs and state setters only), so the panel can depend on them in an effect.
  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const stop = useCallback(() => {
    clearTimer();
    recRef.current?.stop();
  }, [clearTimer]);

  const cancel = useCallback(() => {
    clearTimer();
    const rec = recRef.current;
    if (rec === null) return;
    // Chrome delivers a final result after stop(); detach so it never refills the box.
    rec.onresult = null;
    recRef.current = null;
    setListening(false);
    rec.abort();
  }, [clearTimer]);

  function armTimer() {
    clearTimer();
    timerRef.current = window.setTimeout(stop, VOICE_SILENCE_MS);
  }

  function start() {
    const Ctor = recognitionCtor();
    if (Ctor === null || recRef.current !== null) return;
    const rec = new Ctor();
    const base = getBase().trimEnd();
    rec.lang = recognitionLang();
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (event) => {
      const words = Array.from(event.results, (r) => r[0].transcript)
        .join('')
        .trim();
      onText(base === '' ? words : `${base} ${words}`);
      armTimer();
    };
    rec.onerror = (event) => {
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        setDenied(true);
      }
    };
    rec.onend = () => {
      // DNKADM-0474: an aborted session's late `end` must not clear a NEWER session started by a
      // quick re-tap; only the current recognition may reset the shared state.
      if (recRef.current !== rec) return;
      clearTimer();
      recRef.current = null;
      setListening(false);
    };
    recRef.current = rec;
    setListening(true);
    armTimer();
    try {
      rec.start();
    } catch {
      recRef.current = null;
      clearTimer();
      setListening(false);
    }
  }

  function toggle() {
    if (listening) stop();
    else start();
  }

  // Leaving the panel (or signing out) ends the microphone session.
  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      const rec = recRef.current;
      if (rec !== null) {
        rec.onend = null;
        rec.abort();
      }
    },
    [],
  );

  return { supported, listening, denied, toggle, stop, cancel } satisfies VoiceDictation;
}
